import React, { useEffect, useState } from "react";
import {
  CalendarDaysIcon,
  UserCircleIcon,
  BuildingOffice2Icon,
  EnvelopeIcon,
  ReceiptRefundIcon,
  HashtagIcon,
  MapPinIcon,
  PhoneIcon,
} from "@heroicons/react/24/outline";
import API from "utils/helpers/APIHelper";
import { ENDPOINTS } from "API/apiConstants";

interface LegacyBooking {
  post_id: string | number;
  post_date?: string;
  property_name: string;
  property_id?: string | number;
  check_in: string;
  check_out: string;
  booking_status: "confirmed" | "pending" | "canceled" | "waiting" | string;
  booking_invoice_no?: string;
  first_name?: string;
  user_email?: string;
  owner_id?: string | number;
  owner_email?: string;
  owneralias?: string;
  property_address?: string;
  owner_phone?: string;
  status?: string;
  invoice_number?: string;
}

const STATUS_CONFIG: Record<string, { badge: string; border: string; dot: string; bg: string }> = {
  confirmed: {
    badge: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300",
    border: "border-l-emerald-500",
    dot: "bg-emerald-500",
    bg: "from-emerald-50 to-transparent dark:from-emerald-900/10 dark:to-transparent",
  },
  pending: {
    badge: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
    border: "border-l-amber-500",
    dot: "bg-amber-500",
    bg: "from-amber-50 to-transparent dark:from-amber-900/10 dark:to-transparent",
  },
  canceled: {
    badge: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
    border: "border-l-red-500",
    dot: "bg-red-500",
    bg: "from-red-50 to-transparent dark:from-red-900/10 dark:to-transparent",
  },
  waiting: {
    badge: "bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300",
    border: "border-l-orange-500",
    dot: "bg-orange-500",
    bg: "from-orange-50 to-transparent dark:from-orange-900/10 dark:to-transparent",
  },
};

const DEFAULT_STATUS = {
  badge: "bg-neutral-100 text-neutral-600 dark:bg-neutral-700 dark:text-neutral-300",
  border: "border-l-neutral-400",
  dot: "bg-neutral-400",
  bg: "from-neutral-50 to-transparent dark:from-neutral-800 dark:to-transparent",
};

const formatDateLong = (dateStr: string) => {
  if (!dateStr) return "—";
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  return date.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
};

const formatDateParts = (dateStr: string) => {
  if (!dateStr) return null;
  // Handle "Aug 01, 2026" format directly
  const direct = new Date(dateStr);
  if (!isNaN(direct.getTime())) {
    return {
      day: direct.toLocaleDateString("en-US", { day: "2-digit" }),
      month: direct.toLocaleDateString("en-US", { month: "short" }).toUpperCase(),
      year: direct.toLocaleDateString("en-US", { year: "numeric" }),
    };
  }
  return null;
};

// ─── Skeleton ─────────────────────────────────────────────────────────────────
const SkeletonCard: React.FC = () => (
  <div className="rounded-2xl bg-white dark:bg-neutral-800 shadow-sm border border-neutral-100 dark:border-neutral-700 border-l-4 border-l-neutral-200 dark:border-l-neutral-600 overflow-hidden animate-pulse">
    <div className="p-5 pb-4">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="h-5 w-3/5 bg-neutral-200 dark:bg-neutral-700 rounded-lg" />
        <div className="h-6 w-20 bg-neutral-200 dark:bg-neutral-700 rounded-full" />
      </div>
      <div className="h-3 w-32 bg-neutral-100 dark:bg-neutral-700/50 rounded" />
    </div>
    <div className="px-5 pb-4 grid grid-cols-2 gap-3">
      <div className="rounded-xl bg-neutral-100 dark:bg-neutral-700/50 h-20" />
      <div className="rounded-xl bg-neutral-100 dark:bg-neutral-700/50 h-20" />
    </div>
    <div className="mx-5 mb-4 h-12 rounded-xl bg-neutral-100 dark:bg-neutral-700/40" />
    <div className="px-5 pb-4 flex justify-between">
      <div className="h-3 w-24 bg-neutral-100 dark:bg-neutral-700/40 rounded" />
      <div className="h-3 w-20 bg-neutral-100 dark:bg-neutral-700/40 rounded" />
    </div>
  </div>
);

// ─── Date Box ─────────────────────────────────────────────────────────────────
const DateBox: React.FC<{ label: string; dateStr: string }> = ({ label, dateStr }) => {
  const parts = formatDateParts(dateStr);
  return (
    <div className="flex flex-col items-center justify-center rounded-xl bg-white dark:bg-neutral-700/60 border border-neutral-200 dark:border-neutral-600 p-3 gap-0.5 text-center shadow-sm">
      <span className="text-[10px] font-bold text-neutral-400 dark:text-neutral-500 uppercase tracking-widest mb-1">
        {label}
      </span>
      {parts ? (
        <>
          <span className="text-2xl font-black text-neutral-900 dark:text-neutral-100 leading-none">
            {parts.day}
          </span>
          <span className="text-xs font-semibold text-neutral-500 dark:text-neutral-400">
            {parts.month}
          </span>
          <span className="text-xs text-neutral-400 dark:text-neutral-500">
            {parts.year}
          </span>
        </>
      ) : (
        <span className="text-sm font-semibold text-neutral-700 dark:text-neutral-300">{dateStr}</span>
      )}
    </div>
  );
};

// ─── Booking Card ─────────────────────────────────────────────────────────────
const BookingCard: React.FC<{ booking: LegacyBooking; index: number }> = ({ booking, index }) => {
  const statusKey = (booking.booking_status || booking.status || "").toLowerCase();
  const sc = STATUS_CONFIG[statusKey] || DEFAULT_STATUS;
  const invoiceNo = booking.booking_invoice_no || booking.invoice_number;

  return (
    <div
      className={`rounded-2xl bg-white dark:bg-neutral-800 shadow-sm border border-neutral-100 dark:border-neutral-700 border-l-4 ${sc.border} overflow-hidden transition-shadow hover:shadow-md`}
      style={{ animationDelay: `${index * 60}ms` }}
    >
      {/* Header gradient */}
      <div className={`bg-gradient-to-r ${sc.bg} px-5 pt-5 pb-4`}>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-2 min-w-0 flex-1">
            <BuildingOffice2Icon className="w-5 h-5 flex-shrink-0 text-[#f5694b] mt-0.5" />
            <h3 className="text-base font-bold text-neutral-900 dark:text-neutral-100 leading-snug">
              {booking.property_name || "Unnamed Property"}
            </h3>
          </div>
          <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full capitalize flex-shrink-0 ${sc.badge}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
            {booking.booking_status || booking.status || "unknown"}
          </span>
        </div>
        {booking.property_address && (
          <p className="mt-1 ml-7 flex items-center gap-1 text-xs text-neutral-500 dark:text-neutral-400">
            <MapPinIcon className="w-3 h-3 flex-shrink-0 text-[#f5694b]" />
            {booking.property_address}
          </p>
        )}
        {booking.post_date && (
          <p className="mt-1.5 ml-7 text-xs text-neutral-400 dark:text-neutral-500">
            Booked {formatDateLong(booking.post_date)}
          </p>
        )}
      </div>

      {/* Divider */}
      <div className="mx-5 border-t border-dashed border-neutral-200 dark:border-neutral-700" />

      {/* Dates */}
      <div className="px-5 py-4 grid grid-cols-2 gap-3">
        <DateBox label="Check-in" dateStr={booking.check_in} />
        <DateBox label="Check-out" dateStr={booking.check_out} />
      </div>

      {/* Owner section */}
      {(booking.owneralias || booking.owner_email) && (
        <div className="mx-5 mb-4 flex items-center gap-3 rounded-xl bg-neutral-50 dark:bg-neutral-700/40 border border-neutral-100 dark:border-neutral-600/50 px-4 py-3">
          <div className="w-8 h-8 rounded-full bg-[#f5694b]/10 flex items-center justify-center flex-shrink-0">
            <UserCircleIcon className="w-5 h-5 text-[#f5694b]" />
          </div>
          <div className="min-w-0">
            {booking.owneralias && (
              <p className="text-xs font-semibold text-neutral-700 dark:text-neutral-300 leading-tight">
                Hosted by <span className="capitalize">{booking.owneralias}</span>
              </p>
            )}
            {booking.owner_email && (
              <p className="flex items-center gap-1 text-xs text-neutral-400 dark:text-neutral-500 truncate mt-0.5">
                <EnvelopeIcon className="w-3 h-3 flex-shrink-0" />
                {booking.owner_email}
              </p>
            )}
            {booking.owner_phone && (
              <p className="flex items-center gap-1 text-xs text-neutral-400 dark:text-neutral-500 mt-0.5">
                <PhoneIcon className="w-3 h-3 flex-shrink-0" />
                {booking.owner_phone}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="px-5 pb-4 flex items-center justify-between gap-2 flex-wrap border-t border-neutral-100 dark:border-neutral-700/50 pt-3">
        <span className="flex items-center gap-1 text-xs text-neutral-400 dark:text-neutral-500">
          <HashtagIcon className="w-3 h-3" />
          <span className="font-mono">Ref {booking.post_id}</span>
        </span>
        {invoiceNo && (
          <span className="flex items-center gap-1 text-xs text-neutral-400 dark:text-neutral-500">
            <ReceiptRefundIcon className="w-3 h-3" />
            <span className="font-mono">Invoice {invoiceNo}</span>
          </span>
        )}
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const LegacyBookingsPage: React.FC = () => {
  const [bookings, setBookings] = useState<LegacyBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      setError(null);
      try {
        const data: any = await API.get(ENDPOINTS.LEGACY_BOOKINGS);
        const list = Array.isArray(data) ? data : data?.bookings ?? data?.data ?? [];
        setBookings(list);
      } catch (err: any) {
        setError(err?.response?.data?.message || "Failed to load previous bookings. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  return (
    <div className="nc-LegacyBookingsPage min-h-screen bg-neutral-50 dark:bg-neutral-900">

      {/* Hero header bar */}
      <div className="bg-white dark:bg-neutral-800 border-b border-neutral-100 dark:border-neutral-700 shadow-sm">
        <div className="container max-w-2xl mx-auto px-4 sm:px-6 py-8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#f5694b]/10 flex items-center justify-center flex-shrink-0">
              <CalendarDaysIcon className="w-5 h-5 text-[#f5694b]" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-neutral-900 dark:text-neutral-100 leading-tight">
                Previous Bookings
              </h1>
              <p className="text-sm text-neutral-500 dark:text-neutral-400">
                Your booking history from our previous platform
              </p>
            </div>
          </div>

          {/* Summary pill */}
          {!loading && !error && bookings.length > 0 && (
            <div className="mt-4 inline-flex items-center gap-2 bg-neutral-100 dark:bg-neutral-700 rounded-full px-3 py-1.5 text-xs font-medium text-neutral-600 dark:text-neutral-300">
              <span className="w-1.5 h-1.5 rounded-full bg-[#f5694b]" />
              {bookings.length} booking{bookings.length !== 1 ? "s" : ""} found
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="container max-w-2xl mx-auto px-4 sm:px-6 py-6">

        {/* Loading skeleton */}
        {loading && (
          <div className="flex flex-col gap-4">
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </div>
        )}

        {/* Error state */}
        {!loading && error && (
          <div className="rounded-2xl bg-white dark:bg-neutral-800 shadow-sm border border-red-100 dark:border-red-900/40 p-8 text-center">
            <div className="w-12 h-12 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mx-auto mb-3">
              <CalendarDaysIcon className="w-6 h-6 text-red-400" />
            </div>
            <p className="text-sm font-medium text-red-600 dark:text-red-400">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 text-xs text-[#f5694b] hover:underline font-medium"
            >
              Try again
            </button>
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && bookings.length === 0 && (
          <div className="rounded-2xl bg-white dark:bg-neutral-800 shadow-sm border border-neutral-100 dark:border-neutral-700 p-12 text-center">
            <div className="w-16 h-16 rounded-2xl bg-neutral-100 dark:bg-neutral-700 flex items-center justify-center mx-auto mb-4">
              <CalendarDaysIcon className="w-8 h-8 text-neutral-300 dark:text-neutral-500" />
            </div>
            <h3 className="text-base font-semibold text-neutral-700 dark:text-neutral-300 mb-2">
              No previous bookings found
            </h3>
            <p className="text-sm text-neutral-400 dark:text-neutral-500 max-w-xs mx-auto leading-relaxed">
              Bookings made on our previous platform will appear here once migrated to your account.
            </p>
          </div>
        )}

        {/* Booking cards */}
        {!loading && !error && bookings.length > 0 && (
          <div className="flex flex-col gap-4">
            {bookings.map((booking, i) => (
              <BookingCard key={booking.post_id} booking={booking} index={i} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default LegacyBookingsPage;
